import { FileUploadLabels, FileUploadValidation } from './FileUpload.types';
import { SxProps, Theme } from '@mui/material';

/**
 * Default file validation configuration
 * @constant
 */
export const defaultValidation: FileUploadValidation = {
  maxFileSize: 20 * 1024 * 1024, // 20MB
  acceptedTypes: [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  allowedExtensions: ['.csv', '.xlsx', '.xls'],
};

/**
 * Default labels for FileUpload component
 * @constant
 */
export const defaultLabels: FileUploadLabels = {
  dropzone: 'Drag and drop a file here, or click to select',
  fileSize: 'Maximum file size: 20MB',
  fileTypes: 'Accepted formats: CSV, XLSX, XLS',
  success: 'File uploaded successfully',
  errorFileSize: 'File is too large',
  errorFileType: 'Unsupported file format',
  errorMultipleFiles: 'Please select only one file',
  errorValidation: 'File validation failed',
  instructionsTitle: 'File Requirements:',
};

/**
 * Default instructions for file upload
 * @constant
 */
export const defaultInstructions = [
  'File should contain at least 2 columns',
  'Column 1: Date/timestamp (will be parsed as date/time)',
  'Column 2: Power value in kW or kWh (numbers only, decimals allowed)',
  'Missing values will be handled automatically',
  'Data will be processed to create a weekly power profile',
];

/**
 * Returns custom label if provided, otherwise returns default label or empty string
 * @param customLabel - Custom label provided by user
 * @param defaultLabel - Default label to fallback to
 * @returns The label to use
 */
export const getLabel = (customLabel: string | undefined, defaultLabel: string) => customLabel || defaultLabel || '';

/**
 * Returns appropriate border color based on component state
 * @param error - Error message if any
 * @param isDragging - Whether user is dragging file over dropzone
 * @param readOnly - Whether component is in read-only mode
 * @returns Material-UI theme color string
 */
export const getBorderColor = (error?: string, isDragging?: boolean, readOnly?: boolean) => {
  if (error) {
    return 'error.main';
  }
  if (isDragging) {
    return 'primary.main';
  }
  if (readOnly) {
    return 'action.disabled';
  }
  return 'grey.300';
};

/**
 * Returns appropriate background color based on component state
 * @param isDragging - Whether user is dragging file over dropzone
 * @param readOnly - Whether component is in read-only mode
 * @returns Material-UI theme color string
 */
export const getBackgroundColor = (isDragging?: boolean, readOnly?: boolean) => {
  if (isDragging) {
    return 'action.hover';
  }
  if (readOnly) {
    return 'action.disabledBackground';
  }
  return 'background.paper';
};

/**
 * Returns appropriate text color based on component state and variant
 * @param readOnly - Whether component is in read-only mode
 * @param variant - Text variant ('primary' or 'disabled')
 * @returns Material-UI theme color string
 */
export const getTextColor = (readOnly: boolean, variant: 'disabled' | 'primary') => {
  if (readOnly) {
    return 'text.disabled';
  }
  return variant === 'primary' ? 'text.primary' : 'text.secondary';
};

/**
 * Returns complete dropzone styles object based on component state
 * @param error - Error message if any
 * @param isDragging - Whether user is dragging file over dropzone
 * @param readOnly - Whether component is in read-only mode
 * @param sx - Custom Material-UI sx styles to merge
 * @returns Complete Material-UI sx styles object
 */
export const getDropzoneStyles = (
  error?: string,
  isDragging?: boolean,
  readOnly?: boolean,
  sx?: SxProps<Theme>
): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: { xs: 2, sm: 3 },
  minHeight: { xs: 150, sm: 200 },
  minWidth: { xs: 250, sm: 300 },
  width: '100%',
  maxWidth: '100%',
  border: '2px dashed',
  borderColor: getBorderColor(error, isDragging, readOnly),
  borderRadius: 1,
  backgroundColor: getBackgroundColor(isDragging, readOnly),
  cursor: readOnly ? 'default' : 'pointer',
  opacity: readOnly ? 0.7 : 1,
  pointerEvents: readOnly ? 'none' : 'auto',
  transition: 'all 0.2s',
  ...sx,
});

/**
 * Adds validation error to errors array
 * @param customLabel - Custom error label from user
 * @param defaultLabel - Default error label
 * @param errors - Errors array to add to
 */
export const addValidationError = (customLabel: string | undefined, defaultLabel: string, errors: string[]) => {
  errors.push(getLabel(customLabel, defaultLabel));
};

/**
 * Returns loading overlay styles
 * @returns Material-UI sx styles object for loading overlay
 */
export const getLoadingOverlayStyles = (): SxProps<Theme> => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(2px)',
  borderRadius: 1,
});

/**
 * Returns styles for the upload icon
 * @returns Material-UI sx styles object for upload icon
 */
export const getUploadIconStyles = (): SxProps<Theme> => ({
  fontSize: { xs: 36, sm: 48 },
  color: 'text.secondary',
  mb: 2,
  display: 'block',
  mx: 'auto',
});

/**
 * Returns styles for dropzone text
 * @param variant - Text variant ('main' or 'secondary')
 * @returns Material-UI sx styles object for text
 */
export const getDropzoneTextStyles = (variant: 'main' | 'secondary'): SxProps<Theme> => ({
  textAlign: 'center',
  fontSize: variant === 'main' ? { xs: '0.875rem', sm: '1rem' } : { xs: '0.75rem', sm: '0.875rem' },
});

/**
 * Returns styles for the content wrapper
 * @returns Material-UI sx styles object for content wrapper
 */
export const getContentWrapperStyles = (): SxProps<Theme> => ({
  position: 'relative',
});
